import gql from 'graphql-tag';
import React, {useMemo} from 'react';

import {assert} from 'Asserts';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import {PlateMapType} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import {FillPlateWorklistItemsQuery} from 'samples/plate-maps/fill-plate-modal/__generated/FillPlateWorklistItemsQuery';
import {
  FILL_PLATE_LIST_ITEM_BIOENTITY_FRAGMENT,
  FILL_PLATE_LIST_ITEM_CONTAINER_FRAGMENT,
  getAdditionalTableColumns,
  validateFillPlateListItem,
  WorklistItemNode,
} from 'samples/plate-maps/fill-plate-modal/FillPlateFromModalUtils';
import {ProcedureTestOrderConfig} from 'samples/plate-maps/integrations/procedures/PlateMapTestOrderUtils';
import {
  SelectItemsFromWorklistModal,
  SelectItemsFromWorklistModalProps,
  SelectItemsFromWorklistModalResult,
} from 'worklists/SelectItemsFromWorklistModal';

export const FILL_PLATE_WORKLIST_ITEMS_QUERY = gql`
  query FillPlateWorklistItemsQuery($worklistId: StrictString!) @skipAudit @engComponent(name: INVENTORY) {
    worklist(id: $worklistId) {
      ... on WorklistNode {
        id
        items {
          # BatchNode and PlateNode are just included for type completeness
          ... on BatchNode {
            id
            name
          }
          ... on PlateNode {
            id
            name
          }
          ... on ContainerNode {
            ...FillPlateListItemContainerFragment
          }
          ... on RegistryItemInterface {
            ...FillPlateListItemBioentityFragment
          }
          ... on InaccessibleInfoNode {
            ...InaccessibleInfoGraphQL
          }
        }
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${FILL_PLATE_LIST_ITEM_CONTAINER_FRAGMENT}
  ${FILL_PLATE_LIST_ITEM_BIOENTITY_FRAGMENT}
`;

type WorklistItemNodeAllTypes = ExcludeInaccessible<FillPlateWorklistItemsQuery['worklist']>['items'][number];

function isItemOfValidType(item: WorklistItemNodeAllTypes): item is WorklistItemNode {
  return item.__typename !== 'BatchNode' && item.__typename !== 'PlateNode';
}

function doesResultContainOnlyValidTypes(
  result: SelectItemsFromWorklistModalResult<WorklistItemNodeAllTypes>
): result is SelectItemsFromWorklistModalResult<WorklistItemNode> {
  return result.selectedWorklistItems.every((item) => isItemOfValidType(item));
}

export interface FillPlateFromWorklistModalProps extends Omit<
  SelectItemsFromWorklistModalProps<WorklistItemNodeAllTypes>,
  'onRequestSubmit' | 'validateItem' | 'query'
> {
  onRequestSubmit: (result: SelectItemsFromWorklistModalResult<WorklistItemNode>) => void;
  destinationPlateId: string | null;
  procedureTestOrderConfig: ProcedureTestOrderConfig | null;
  plateMapType: PlateMapType;
}

/**
 * A modal for selecting containable bioentities and containers from a worklist. Allows selecting a subset of items.
 * @param onRequestClose Callback executed when the modal is closed.
 * @param onRequestSubmit Callback executed with the selected item api ids when flow is complete.
 * @param destinationPlateId ID of the plate being transfered into if it already exists
 * @param procedureTestOrderConfig Procedure config to validate test orders within assay context
 * @constructor
 */
export default function FillPlateFromWorklistModal({
  onRequestClose,
  onRequestSubmit,
  destinationPlateId,
  procedureTestOrderConfig,
  plateMapType,
}: FillPlateFromWorklistModalProps): JSX.Element {
  const additionalColumns = useMemo(() => getAdditionalTableColumns(), []);
  return (
    <SelectItemsFromWorklistModal<FillPlateWorklistItemsQuery>
      onRequestClose={onRequestClose}
      onRequestSubmit={(result) => {
        // Guaranteed by validateItem
        assert(doesResultContainOnlyValidTypes(result), 'Plates and batches cannot be used to fill plates.');
        onRequestSubmit(result);
      }}
      validateItem={(item) => {
        if (!isItemOfValidType(item)) {
          return {
            isSelectorDisabled: true,
            tooltipText: 'Plates and batches cannot be used to fill plates.',
          };
        }
        return validateFillPlateListItem({
          listItemNode: item,
          destinationPlateId,
          procedureTestOrderConfig,
          plateMapType,
        });
      }}
      query={FILL_PLATE_WORKLIST_ITEMS_QUERY}
      additionalColumns={additionalColumns}
    />
  );
}
